import { useState, useEffect } from "react";
import "./css/adminregion.css"
import { getRegionStatistics, getCustStatistics } from "../../utils/adminRegionService";
import { apiGet } from "../../utils/apiClient";

const normalizeAddrDo = (addrDo) => {
    const mapping = {
        '전라남도': '전남',
        '전라북도': '전북',
        '경상남도': '경남',
        '경상북도': '경북',
        '충청남도': '충남',
        '충청북도': '충북',
        '강원도': '강원',
        '경기도': '경기',
        '제주특별자치도': '제주',
        '서울특별시': '서울',
        '부산광역시': '부산',
        '대구광역시': '대구',
        '인천광역시': '인천',
        '광주광역시': '광주',
        '대전광역시': '대전',
        '울산광역시': '울산',
        '세종특별자치시': '세종',
    };
    return mapping[addrDo] ?? addrDo;
};

const AdminRegion = () => {
    const [selectedState, setSelectedState] = useState("");
    const [selectedStateName, setSelectedStateName] = useState("");
    const [selectedRegion, setSelectedRegion] = useState("");
    const [selectedRegionName, setSelectedRegionName] = useState("");
    const [selectedSubRegion, setSelectedSubRegion] = useState("");
    const [selectedSubRegionName, setSelectedSubRegionName] = useState("");
    const [selectedTab, setSelectedTab] = useState("franchise"); // franchise or general
    const [franchiseData, setFranchiseData] = useState(null);
    const [generalData, setGeneralData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [stateOptions, setStateOptions] = useState([]);
    const [regionOptions, setRegionOptions] = useState([]);
    const [subRegionOptions, setSubRegionOptions] = useState([]);

    useEffect(() => {
        const loadStates = async () => {
            try {
                const response = await apiGet('/addr');
                if (response.ok) {
                    const data = await response.json();
                    setStateOptions(data.map(item => ({ label: item.addrName, value: item.cd })));
                } else {
                    console.error('Failed to load states, status:', response.status);
                }
            } catch (error) {
                console.error('Failed to load states', error);
            }
        };
        loadStates();
    }, []);


    const loadRegions = async (cd) => {
        try {
            const response = await apiGet(`/addr?cd=${cd}`);
            if (response.ok) {
                const data = await response.json();
                setRegionOptions(data.map(item => ({ label: item.addrName, value: item.cd })));
            } else {
                console.error('Failed to load regions');
            }
        } catch (error) {
            console.error('Failed to load regions', error);
        }
    };

    // 선택한 시/군/구 하위에 '구'로 끝나는 항목이 있으면(예: 분당구) 한 단계 더 선택하게 함
    const loadSubRegions = async (cd) => {
        try {
            const response = await apiGet(`/addr?cd=${cd}`);
            if (response.ok) {
                const data = await response.json();
                const options = data.map(item => ({ label: item.addrName, value: item.cd }));
                const hasSubRegions = options.some(opt => opt.label.endsWith('구'));
                setSubRegionOptions(hasSubRegions ? options : []);
            } else {
                console.error('Failed to load subregions');
            }
        } catch (error) {
            console.error('Failed to load subregions', error);
        }
    };

    const handleStateChange = (e) => {
        const cd = e.target.value;
        setSelectedState(cd);
        const selectedOption = stateOptions.find(opt => opt.value === cd);
        setSelectedStateName(selectedOption ? selectedOption.label : "");
        setSelectedRegion("");
        setSelectedRegionName("");
        setSelectedSubRegion("");
        setSelectedSubRegionName("");
        setRegionOptions([]);
        setSubRegionOptions([]);
        if (cd) {
            loadRegions(cd);
        }
    };

    const handleRegionChange = (e) => {
        const cd = e.target.value;
        setSelectedRegion(cd);
        const selectedOption = regionOptions.find(opt => opt.value === cd);
        setSelectedRegionName(selectedOption ? selectedOption.label : "");
        setSelectedSubRegion("");
        setSelectedSubRegionName("");
        setSubRegionOptions([]);
        if (cd) {
            loadSubRegions(cd);
        }
    };

    const handleSubRegionChange = (e) => {
        const cd = e.target.value;
        setSelectedSubRegion(cd);
        const selectedOption = subRegionOptions.find(opt => opt.value === cd);
        setSelectedSubRegionName(selectedOption ? selectedOption.label : "");
    };

    const hasSubRegion = subRegionOptions.length > 0;
    const finalRegionLabel = selectedSubRegionName || selectedRegionName;

    const handleSearch = async () => {
        if (!selectedRegionName || (hasSubRegion && !selectedSubRegionName)) {
            setError("지역을 선택해주세요.");
            return;
        }

        setLoading(true);
        setError(null);

        // 특별시/광역시/특별자치시 여부
        const isMetropolitan = ["특별시", "광역시", "특별자치시"].some(s => selectedStateName.includes(s));

        // "수원시 영통구" 처럼 API가 시+구를 합쳐서 반환하는 경우 분리
        const regionParts = selectedRegionName.split(' ');
        const hasCombinedGuGun = regionParts.length === 2 && (regionParts[1].endsWith('구') || regionParts[1].endsWith('군'));
        const parsedSi = hasCombinedGuGun ? regionParts[0] : selectedRegionName;
        const parsedGuGun = hasCombinedGuGun ? regionParts[1] : "";

        // hasSubRegion + !isMetropolitan : 도 → 시 → 구  (경기도 성남시 분당구)
        // isMetropolitan                 : 특별/광역시 → 구 (서울특별시 강남구)  → addrSi 빈값
        // !hasSubRegion + !isMetropolitan: 도 → 시(구없음) (경기도 여주시)      → addrGuGun 빈값
        // hasCombinedGuGun               : API가 "수원시 영통구" 형태로 반환하는 경우 분리
        const addrParams = {
            addrDo: normalizeAddrDo(selectedStateName),
            addrSi: isMetropolitan ? "" : parsedSi,
            addrGuGun: hasSubRegion ? selectedSubRegionName
                     : isMetropolitan ? selectedRegionName
                     : parsedGuGun,
        };

        try {
            const [franchiseResult, generalResult] = await Promise.all([
                getRegionStatistics(addrParams),
                getCustStatistics(addrParams)
            ]);

            if (franchiseResult.success && generalResult.success) {
                const franchiseData = franchiseResult.data;
                const generalData = generalResult.data;

                // 가맹점 데이터 변환
                const franchiseLabels = franchiseData.map(item => `${item.month}월`);
                const franchiseSellingPrices = franchiseData.map(item => item.totalSellingPrice); // 원 단위
                const franchiseSellingCounts = franchiseData.map(item => item.totalSellingCnt);

                setFranchiseData({
                    labels: franchiseLabels,
                    sellingPrices: franchiseSellingPrices,
                    sellingCounts: franchiseSellingCounts
                });

                // 고객 데이터 변환
                const generalLabels = generalData.map(item => `${item.month}월`);
                const generalSellingPrices = generalData.map(item => item.totalSellingPrice); // 원 단위
                const generalSellingCounts = generalData.map(item => item.totalSellingCnt);

                setGeneralData({
                    labels: generalLabels,
                    sellingPrices: generalSellingPrices,
                    sellingCounts: generalSellingCounts
                });
            } else {
                setError(franchiseResult.error || generalResult.error);
                setFranchiseData(null);
                setGeneralData(null);
            }
        } catch (err) {
            console.error("API 요청 실패:", err);
            setError("데이터 조회에 실패했습니다. 다시 시도해주세요.");
            setFranchiseData(null);
            setGeneralData(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="adminregion-container">
            <div className="adminregion-wrap">
                <div className="adminregion-filter-wrap">
                    <div className="adminregion-select-wrap">
                        <select name="state" id="state" value={selectedState} onChange={handleStateChange} className="adminregion-select-state">
                            <option value="">전체</option>
                            {stateOptions.map((state) => (
                                <option key={state.value} value={state.value}>{state.label}</option>
                            ))}
                        </select>

                        <select name="region" id="region" value={selectedRegion} onChange={handleRegionChange} className="adminregion-select-region">
                            <option value="">전체</option>
                            {regionOptions.map((region) => (
                                <option key={region.value} value={region.value}>{region.label}</option>
                            ))}
                        </select>

                        {hasSubRegion && (
                            <select name="subregion" id="subregion" value={selectedSubRegion} onChange={handleSubRegionChange} className="adminregion-select-subregion" disabled={!selectedRegion}>
                                <option value="">전체</option>
                                {subRegionOptions.map((subRegion) => (
                                    <option key={subRegion.value} value={subRegion.value}>{subRegion.label}</option>
                                ))}
                            </select>
                        )}

                        <button className="adminregion-search-button" onClick={handleSearch} disabled={loading || !selectedRegionName || (hasSubRegion && !selectedSubRegionName)}>
                            {loading ? "조회 중..." : "검색"}
                        </button>
                    </div>
                </div>

                <div className="adminregion-static-wrap">
                    <div className="adminregion-static-graph">
                        {error && (
                            <div className="adminregion-error-message" style={{ color: 'red', padding: '10px', marginBottom: '10px' }}>
                                {error}
                            </div>
                        )}
                        {(franchiseData || generalData) && (
                            <>
                                <div className="adminregion-user-type-tabs">
                                    <button
                                        className={`adminregion-user-tab ${selectedTab === 'franchise' ? 'active' : ''}`}
                                        onClick={() => setSelectedTab('franchise')}
                                    >
                                        가맹점
                                    </button>
                                    <button
                                        className={`adminregion-user-tab ${selectedTab === 'general' ? 'active' : ''}`}
                                        onClick={() => setSelectedTab('general')}
                                    >
                                        일반사용자
                                    </button>
                                </div>

                                <div className="adminregion-chart-area">
                                    {(selectedTab === 'franchise' ? franchiseData : generalData) ? (
                                        <div className="adminregion-chart">
                                            <h3>{finalRegionLabel} - {selectedTab === 'franchise' ? '가맹점 월별 통계' : '일반사용자 월별 통계'}</h3>
                                            <div className="chart-placeholder">
                                                <p>월별 판매액 및 판매건수</p>
                                                <div className="chart-bars">
                                                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
                                                        const data = selectedTab === 'franchise' ? franchiseData : generalData;
                                                        const monthLabel = `${month}월`;
                                                        const dataIndex = data.labels.indexOf(monthLabel);
                                                        const hasData = dataIndex !== -1;
                                                        const priceValue = hasData ? data.sellingPrices[dataIndex] : 0;
                                                        const countValue = hasData ? data.sellingCounts[dataIndex] : 0;
                                                        const maxPrice = Math.max(...data.sellingPrices, 1);
                                                        const priceHeight = hasData ? Math.max((priceValue / maxPrice) * 100, 5) : 0;

                                                        return (
                                                            <div key={month} className={`chart-bar-group${!hasData ? ' chart-bar-group-empty' : ''}`}>
                                                                <div className="chart-bar-container">
                                                                    <div
                                                                        className="chart-bar price-bar"
                                                                        style={{ height: hasData ? `${priceHeight}%` : '5px', opacity: hasData ? 1 : 0.18 }}
                                                                    />
                                                                </div>
                                                                {hasData && (
                                                                    <div className="chart-tooltip">
                                                                        <div className="chart-tooltip-row">
                                                                            <span className="chart-tooltip-dot price-dot" />
                                                                            <span>{priceValue.toLocaleString()}원</span>
                                                                        </div>
                                                                        <div className="chart-tooltip-row">
                                                                            <span className="chart-tooltip-dot count-dot" />
                                                                            <span>{countValue}건</span>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                <span className={`chart-label${!hasData ? ' chart-label-empty' : ''}`}>{monthLabel}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                <div className="chart-legend">
                                                    <div className="legend-item">
                                                        <div className="legend-color price-bar"></div>
                                                        <span>판매액 (호버 시 판매건수 표시)</span>
                                                    </div>
                                                    <div className="legend-item" style={{ display: 'none' }}>
                                                        <div className="legend-color count-bar"></div>
                                                        <span>판매건수</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="adminregion-no-data">
                                            <p>데이터를 불러오는 중입니다...</p>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        {!franchiseData && !generalData && (
                            <div className="adminregion-no-data">
                                <p>지역을 선택하고 검색 버튼을 클릭해주세요</p>
                            </div>
                        )}
                    </div>
                    <div className="adminregion-static-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>월</th>
                                    <th>판매액</th>
                                    <th>판매건수</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(selectedTab === 'franchise' ? franchiseData : generalData) ? (
                                    (selectedTab === 'franchise' ? franchiseData : generalData).labels.map((label, index) => {
                                        const data = selectedTab === 'franchise' ? franchiseData : generalData;
                                        return (
                                            <tr key={index}>
                                                <td>{label}</td>
                                                <td>{data.sellingPrices[index].toLocaleString()}</td>
                                                <td>{data.sellingCounts[index]}</td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="3">데이터가 없습니다</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminRegion;